import type { MutationInvalidator } from '~/hooks/useMutationInvalidator';
import { capitalizeFirstLetter } from '../layout/utils';

export function getApiErrorMessage(error: any) {
  const message = error?.message;

  if (!message) {
    return 'Something went wrong, please try refreshing the application.';
  }

  return capitalizeFirstLetter(message);
}

type SingleAssetValue = string | undefined | null;
type ArrayAssetValue = string[];

type LinkPayloadFn<Asset, R> = (payload: {
  linkAction: 'link' | 'unlink';
  assetType: Asset;
  assetId: string;
}) => Promise<MutationInvalidator<R>>;

export type BulkLinkPayloads<SingleAssetType, ArrayAssetType> = (
  | { assetType: SingleAssetType; assetId: SingleAssetValue }
  | { assetType: ArrayAssetType; assetIds: ArrayAssetValue; mode: 'link' | 'unlink' | 'override' }
)[];

export type LinkAssetPayload<Asset> = { linkAction: 'link' | 'unlink'; assetType: Asset; assetId: string };

export type LinkAssetMap<SingleAsset extends string, ArrayAsset extends string> =
  | Record<SingleAsset, SingleAssetValue>
  | Record<ArrayAsset, ArrayAssetValue | undefined | null>;

export async function linkMultipleAssets<SingleAsset extends string, ArrayAsset extends string, R>(
  linkPayloads: BulkLinkPayloads<SingleAsset, ArrayAsset>,
  assetMap: LinkAssetMap<SingleAsset, ArrayAsset>,
  fn: LinkPayloadFn<SingleAsset | ArrayAsset, R>,
) {
  let lastLink: MutationInvalidator<R> = {};
  for (const payload of linkPayloads) {
    lastLink = (await linkAssets<SingleAsset, ArrayAsset, R>(payload, assetMap, fn)) ?? lastLink;
  }

  return lastLink;
}

async function linkAssets<SingleAsset extends string, ArrayAsset extends string, R>(
  // Incoming Assets to Link
  linkPayloads: BulkLinkPayloads<SingleAsset, ArrayAsset>[number],
  // Map of AssetType to AssetId(s) on the target
  assetMap: Record<SingleAsset, SingleAssetValue> | Record<ArrayAsset, ArrayAssetValue | undefined | null>,
  // Function to call to link/unlink the asset
  fn: LinkPayloadFn<SingleAsset | ArrayAsset, R>,
) {
  // If the payload is a single asset, link/unlink the asset
  if ('assetId' in linkPayloads) {
    const currentAssetId = assetMap[linkPayloads.assetType] as SingleAssetValue;

    // If the current asset id is not the same as the incoming asset id, link/unlink the asset
    if (currentAssetId !== linkPayloads.assetId) {
      // If the incoming asset id is truthy, link the asset
      if (linkPayloads.assetId) {
        return await fn({ linkAction: 'link', assetType: linkPayloads.assetType, assetId: linkPayloads.assetId });
      }

      // If the incoming asset id is falsey and the current asset id is truthy, unlink the asset
      if (currentAssetId) {
        return await fn({ linkAction: 'unlink', assetType: linkPayloads.assetType, assetId: currentAssetId as string });
      }
    }
  } else {
    let lastLink: MutationInvalidator<R> | undefined;

    if (linkPayloads.mode === 'override') {
      // If the payload is multiple assets, link/unlink the assets
      const currentAssetIds = assetMap[linkPayloads.assetType] as ArrayAssetValue | undefined | null;
      // Get the incoming asset ids that are not in the current asset ids
      const incomingAssetIds = linkPayloads.assetIds.filter((assetId) => !currentAssetIds?.includes(assetId));
      // Get the current asset ids that are not in the incoming asset ids
      const removingAssetIds = currentAssetIds?.filter((assetId) => !linkPayloads.assetIds.includes(assetId)) ?? [];

      // Link the incoming asset ids that are not in the current asset ids
      for (const assetId of incomingAssetIds) {
        lastLink = await fn({ linkAction: 'link', assetType: linkPayloads.assetType, assetId });
      }

      // Unlink the current asset ids that are not in the incoming asset ids
      for (const assetId of removingAssetIds) {
        lastLink = await fn({ linkAction: 'unlink', assetType: linkPayloads.assetType, assetId });
      }
    } else if (linkPayloads.mode === 'link') {
      for (const assetId of linkPayloads.assetIds) {
        lastLink = await fn({ linkAction: 'link', assetType: linkPayloads.assetType, assetId });
      }
    } else if (linkPayloads.mode === 'unlink') {
      for (const assetId of linkPayloads.assetIds) {
        lastLink = await fn({ linkAction: 'unlink', assetType: linkPayloads.assetType, assetId });
      }
    }

    return lastLink;
  }
}

export function addToQueryListData<R>(list: R[], data: R) {
  return [data, ...list];
}

export function removeFromQueryListData<R>(list: R[], idKey: keyof R, id: string) {
  return list.filter((item) => item[idKey] !== id);
}

export function updateQueryListData<R>(list: R[], data: R, idKey: keyof R) {
  return list.map((item) => (item[idKey] === data[idKey] ? data : item));
}

export function addOrUpdateQueryRecordData<R>(record: Record<string, R>, data: R, idKey: keyof R) {
  return { ...record, [data[idKey] as string]: data };
}

export function removeFromQueryRecordData<R>(record: Record<string, R>, id: string) {
  delete record[id];
  return record;
}
